import { useEffect } from 'react'

// Types for structured data
interface FAQItem {
  question: string
  answer: string
}

interface BreadcrumbItem {
  name: string
  url: string
}

interface ArticleData {
  title: string
  description: string
  image: string
  datePublished: string
  dateModified?: string
  author: string
  category?: string
}

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  noIndex?: boolean
  ogType?: 'website' | 'article'
  ogImage?: string
  article?: ArticleData
  faqs?: FAQItem[]
  breadcrumbs?: BreadcrumbItem[]
  includeServiceSchema?: boolean
  includeRatingSchema?: boolean
}

// Site-wide constants
const SITE_NAME = 'Bolletta Amica'
const SITE_URL = 'https://bollettaamica.com'
const DEFAULT_DESCRIPTION = 'Confronta gratuitamente le migliori offerte di luce, gas, fibra e streaming. Risparmia sulle bollette con Bolletta Amica!'
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`
const LOGO_URL = `${SITE_URL}/logo.png`

// Organization Schema (used globally)
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Bolletta Amica',
  alternateName: 'BollettaAmica',
  url: SITE_URL,
  logo: LOGO_URL,
  description: 'Comparatore gratuito di tariffe luce, gas, fibra e streaming in Italia',
  foundingDate: '2024',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Via Alessandro Volta 24',
    addressLocality: 'Sesto San Giovanni',
    postalCode: '20099',
    addressRegion: 'MI',
    addressCountry: 'IT',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@bollettaamica.com',
    contactType: 'customer service',
    availableLanguage: 'Italian',
  },
  sameAs: [
    'https://www.facebook.com/bollettaamica',
    'https://www.instagram.com/bollettaamica',
    'https://it.trustpilot.com/review/bollettaamica.com',
  ],
}

// WebSite Schema with SearchAction
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/il-blog-di-carletto?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
}

// Generate FAQPage Schema
export function generateFAQSchema(faqs: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

// Generate Article Schema
export function generateArticleSchema(article: ArticleData, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image.startsWith('http') ? article.image : `${SITE_URL}${article.image}`,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      '@type': 'Person',
      name: article.author,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    articleSection: article.category,
  }
}

// Generate BreadcrumbList Schema
export function generateBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
  }
}

// Generate Service Schema for comparison services
export function generateServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Confronto Tariffe Energia e Telecomunicazioni',
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    description: 'Servizio gratuito di confronto tariffe per luce, gas, fibra internet e piattaforme streaming',
    areaServed: {
      '@type': 'Country',
      name: 'Italia',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Servizi di Confronto',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Confronto Tariffe Luce e Gas',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Confronto Offerte Fibra Internet',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Confronto Piattaforme Streaming',
          },
        },
      ],
    },
  }
}

// Aggregate Rating Schema for Trustpilot
export function generateAggregateRatingSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '2400',
      reviewCount: '2400',
    },
  }
}

// Main SEO Component
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  canonical,
  noIndex = false,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  article,
  faqs,
  breadcrumbs,
  includeServiceSchema = false,
  includeRatingSchema = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Confronta Tariffe Luce, Gas e Fibra`
  const fullCanonical = canonical ? (canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`) : undefined
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`

  useEffect(() => {
    // Update document title
    document.title = fullTitle

    // Helper to update/create meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name'
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        document.head.appendChild(element)
      }
      element.content = content
    }

    // Helper to update/create link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement
      if (!element) {
        element = document.createElement('link')
        element.rel = rel
        document.head.appendChild(element)
      }
      element.href = href
    }

    // Basic meta tags
    setMetaTag('description', description)
    if (keywords) setMetaTag('keywords', keywords)
    setMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow')

    // Canonical URL
    if (fullCanonical) {
      setLinkTag('canonical', fullCanonical)
    }

    // Open Graph tags
    setMetaTag('og:title', fullTitle, true)
    setMetaTag('og:description', description, true)
    setMetaTag('og:type', ogType, true)
    setMetaTag('og:image', fullOgImage, true)
    setMetaTag('og:site_name', SITE_NAME, true)
    setMetaTag('og:locale', 'it_IT', true)
    if (fullCanonical) setMetaTag('og:url', fullCanonical, true)

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', fullTitle)
    setMetaTag('twitter:description', description)
    setMetaTag('twitter:image', fullOgImage)

    // Clean up old JSON-LD scripts
    document.querySelectorAll('script[data-seo-schema]').forEach((el) => el.remove())

    // Add JSON-LD structured data
    const addSchema = (schema: object, id: string) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-seo-schema', id)
      script.textContent = JSON.stringify(schema)
      document.head.appendChild(script)
    }

    // Always add Organization and WebSite schemas
    addSchema(organizationSchema, 'organization')
    addSchema(websiteSchema, 'website')

    // Add FAQ schema if provided
    if (faqs && faqs.length > 0) {
      addSchema(generateFAQSchema(faqs), 'faq')
    }

    // Add Article schema if provided
    if (article && fullCanonical) {
      addSchema(generateArticleSchema(article, fullCanonical), 'article')
    }

    // Add Breadcrumb schema if provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      addSchema(generateBreadcrumbSchema(breadcrumbs), 'breadcrumb')
    }

    // Add Service schema if requested (for homepage)
    if (includeServiceSchema) {
      addSchema(generateServiceSchema(), 'service')
    }

    // Add AggregateRating schema if requested (for homepage)
    if (includeRatingSchema) {
      addSchema(generateAggregateRatingSchema(), 'rating')
    }

    // Cleanup function
    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach((el) => el.remove())
    }
  }, [fullTitle, description, keywords, noIndex, fullCanonical, ogType, fullOgImage, article, faqs, breadcrumbs, includeServiceSchema, includeRatingSchema])

  return null // This component only manages head elements
}

// Export utility for pages
export { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION }
